import React, { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";

import {
  getStakeholderRelationships,
  getStakeholderRelationshipsByEntity,
} from "../logic/stakeholderSlice";
import { createStakeholderRelationship } from "../logic/stakeholderThunks";

import {
  EntityType,
  RelatedAs,
  RELATED_AS_LABELS,
  KEY_PERSONNEL_RELATED_AS,
} from "../../../api/stakeholder_relationships";
import { EMPLOYEE_TYPE_OPTION_ROLES } from "../../../api/creditAccounts";

import AddNewBorrowerModal from "./AddNewBorrowerModal";
import AddNewGuarantorModal from "./AddNewGuarantorModal";
import AddNewKeyPersonnelModal from "./AddNewKeyPersonnelModal";

import StakeholderSelectionList from "./StakeholderSelectionList";
import ManagementModal from "../../../components/ui/modals/ManagementModal";
import { AddButton } from "./AddStakeholderButton";
import { showErrorToast, showSuccessToast } from "../../../lib/toast";
import { getAccountProductInfoByAccountNumber } from "../../creditAccounts/logic/slice";

interface AddStakeholderModalProps {
  accountNumber: string;
  relatedAs: RelatedAs;
  entityType?: EntityType;
  entityId?: string;
  onClose: () => void;
  onBack?: () => void;
}

// NOTE - 29th Aug, 2026.
// `RelatedAs.EMPLOYEE` will be deprecated eventually. But, the exclusion `excludedStakeholderIds` 
// does not check for entity type so during a transitionary period, Employee names are mapped to
// the CASE entity can still show up, which we want to avoid.
const EMPLOYEE_ROLES_TO_EXCLUDE = [
  RelatedAs.EMPLOYEE,
  ...EMPLOYEE_TYPE_OPTION_ROLES,
];

const AddStakeholderModal: React.FC<AddStakeholderModalProps> = ({
  accountNumber,
  relatedAs,
  entityType = EntityType.LOAN_ACCOUNT,
  entityId,
  onClose,
  onBack,
}) => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();
  const allRelationships = useAppSelector(getStakeholderRelationships);
  const displayAccountNumber = useAppSelector(
    (state) =>
      getAccountProductInfoByAccountNumber(state, accountNumber)
        ?.ext_account_number || "--",
  );

  const [selectedStakeholderIds, setSelectedStakeholderIds] = useState<
    string[]
  >([]);

  const [isLoading, setIsLoading] = useState(false);
  const [showAddNewModal, setShowAddNewModal] = useState(false);

  const targetEntityType = entityType;
  const targetEntityId = entityId ?? accountNumber;

  const rolesToExclude = useMemo(() => {
    if (relatedAs === RelatedAs.CO_BORROWER ||  relatedAs === RelatedAs.PRIMARY_BORROWER) {
      return [RelatedAs.CO_BORROWER, RelatedAs.PRIMARY_BORROWER, RelatedAs.BORROWER];
    }
    return [relatedAs];
  }, [relatedAs]);

  const existingRelationships = useAppSelector((state) =>
    getStakeholderRelationshipsByEntity(
      state,
      targetEntityType,
      targetEntityId,
      rolesToExclude,
    ),
  );

  const excludedStakeholderIds = useMemo(() => {
    const currentEntityStakeholderIds = existingRelationships.map(
      (relationship) => relationship.stakeholder_id,
    );

    const employeeStakeholderIds = allRelationships
      .filter((relationship) => EMPLOYEE_ROLES_TO_EXCLUDE.includes(relationship.related_as))
      .map((relationship) => relationship.stakeholder_id);

    return Array.from(new Set([...currentEntityStakeholderIds, ...employeeStakeholderIds]));
  }, [existingRelationships, allRelationships]);

  const modalTitle = `Add ${
    RELATED_AS_LABELS[relatedAs] ?? "Stakeholder"
  }`;

  const handleSubmit = async () => {
    if (!caseId || selectedStakeholderIds.length === 0) {
      return;
    }

    try {
      setIsLoading(true);

      await Promise.all(
        selectedStakeholderIds.map((stakeholderId) =>
          dispatch(
            createStakeholderRelationship({
              caseId,
              stakeholderId,
              request: {
                entity_type: targetEntityType,
                entity_id: targetEntityId,
                related_as: relatedAs,
              },
            }),
          ).unwrap(),
        ),
      );

      showSuccessToast(`${RELATED_AS_LABELS[relatedAs] ?? "Stakeholder"} added successfully.`);
      onClose();
    } catch (error) {
      showErrorToast(`Failed to add ${RELATED_AS_LABELS[relatedAs] ?? "stakeholder"}. Please try again.`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) {
      return;
    }

    setSelectedStakeholderIds([]);
    setShowAddNewModal(false);
    onClose();
  };

  const handleBack = onBack ?? handleClose;

  const renderAddNewModal = () => {
    if (relatedAs === RelatedAs.GUARANTOR) {
      return (
        <AddNewGuarantorModal
          accountNumber={accountNumber}
          onClose={handleClose}
        />
      );
    }

    if (KEY_PERSONNEL_RELATED_AS.has(relatedAs)) {
      return (
        <AddNewKeyPersonnelModal
          accountNumber={accountNumber}
          primaryBorrowerStakeholderId={targetEntityId}
          relatedAs={relatedAs}
          onClose={handleClose}
        />
      );
    }

    return (
      <AddNewBorrowerModal
        accountNumber={accountNumber}
        relatedAs={relatedAs}
        onClose={handleClose}
      />
    );
  };

  return (
    <>
      {!showAddNewModal && (
        <ManagementModal
          title={modalTitle}
          subtitle={displayAccountNumber}
          subtitleClassName="text-[12px] leading-4 text-[#737373]"
          onSubmit={handleSubmit}
          onBack={handleBack}
          onClose={handleClose}
          submitEnabled={selectedStakeholderIds.length > 0}
          isLoading={isLoading}
        >
          <div className="flex w-full flex-col gap-3">
            <StakeholderSelectionList
              excludedStakeholderIds={excludedStakeholderIds}
              onSelectionChange={setSelectedStakeholderIds}
            />

            <AddButton
              onClick={() => setShowAddNewModal(true)}
            />
          </div>
        </ManagementModal>
      )}

      {showAddNewModal && renderAddNewModal()}
    </>
  );
};

export default AddStakeholderModal;
